import { Controller } from '@hotwired/stimulus';

/**
 * Clipboard Controller — bouton « Copier » : copie `text` dans le presse-papiers, puis remplace
 * l'icône par une coche et le `title` par « Copié ! » pendant 2 s.
 *
 * Values:
 * - text (String): le texte copié
 *
 * Actions:
 * - copy: copie le texte (arrête la propagation et l'action par défaut du clic)
 *
 * Events (bouillonnent, pour un hôte qui annonce le résultat, ex. `share-receipt`) :
 * - clipboard:copied — copie réussie, `detail.text` = le texte copié
 * - clipboard:failed — `writeText` rejeté (aucun retour visuel de succès)
 *
 * @example
 * <button type="button" title="Copier le lien" data-controller="clipboard"
 *         data-clipboard-text-value="https://…" data-action="clipboard#copy">
 *   <svg class="h-4 w-4" aria-hidden="true">…</svg>
 * </button>
 */
export default class extends Controller {
  static values = {
    text: String,
  };

  connect() {
    this.restoreTimeout = null;
  }

  disconnect() {
    if (this.restoreTimeout) clearTimeout(this.restoreTimeout);
  }

  async copy(event) {
    event?.stopPropagation();
    event?.preventDefault();
    const text = this.textValue;
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      this.dispatch('failed');
      return;
    }
    this.showSuccess();
    this.dispatch('copied', { detail: { text } });
  }

  showSuccess() {
    const button = this.element;
    const originalIcon = button.querySelector('svg');

    if (!originalIcon) return;

    // Save original state
    const originalHTML = originalIcon.outerHTML;
    const sizeClasses = originalIcon.getAttribute('class') || 'h-5 w-5';
    const originalTitle = button.getAttribute('title');

    // Replace icon with checkmark and update tooltip
    originalIcon.outerHTML = `<svg class="${sizeClasses}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
    </svg>`;
    button.setAttribute('title', 'Copié !');

    // Restore after 2 seconds
    this.restoreTimeout = setTimeout(() => {
      const checkIcon = button.querySelector('svg');
      if (checkIcon) {
        checkIcon.outerHTML = originalHTML;
      }
      if (originalTitle) {
        button.setAttribute('title', originalTitle);
      }
      this.restoreTimeout = null;
    }, 2000);
  }
}
